import React from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { AdminPartnerPaymentRequestRow } from "../../../../services/PartnerPaymentRequestService";
import Partner from "../../../../models/Partner";
import { GroupedStationRequestDetailsResponse } from "../../../../services/PartnerPaymentRequestService";
import PartnerPayoutDetailsContent, {
	formatPaymentRequestTime,
} from "./PartnerPayoutDetailsContent";

interface PaymentHistoryModalProps {
	isOpen: boolean;
	onClose: () => void;
	stationName: string;
	/** Only this payment cycle (Vietnam month + 1st/2nd cycle), not all partner requests. */
	cycleLabel?: string | null;
	cyclePeriod?: 1 | 2 | null;
	vnYear?: number | null;
	vnMonth?: number | null;
	requests: AdminPartnerPaymentRequestRow[];
	loading?: boolean;
	error?: string | null;
	formatTime?: (time: string | null | undefined) => string;
	partnerBankDetail?: Partner | null;
	cycleDetails?: GroupedStationRequestDetailsResponse | null;
	/** From grouped table row when API does not return partner currency (keeps amounts formatted with code). */
	listRowCurrencyCode?: string | null;
}

const ModalOverlay = styled.div`
	position: fixed;
	inset: 0;
	background-color: rgba(15, 23, 42, 0.58);
	backdrop-filter: blur(2px);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 1000000;
	padding: 20px;
`;

const ModalContainer = styled.div`
	width: 1040px;
	max-width: 96vw;
	max-height: 88vh;
	background: #ffffff;
	border-radius: 16px;
	box-shadow: 0 20px 45px rgba(15, 23, 42, 0.26);
	display: flex;
	flex-direction: column;
	overflow: hidden;
`;

const CloseButton = styled.button`
	border: none;
	background: transparent;
	color: #64748b;
	font-size: 24px;
	line-height: 1;
	cursor: pointer;
	padding: 4px;
	border-radius: 8px;
	flex-shrink: 0;
	transition: background-color 0.15s ease, color 0.15s ease;

	&:hover {
		background-color: #f1f5f9;
		color: #334155;
	}
`;

const Body = styled.div`
	padding: 18px 22px 22px;
	overflow-y: auto;
`;

export default function PaymentHistoryModal({
	isOpen,
	onClose,
	stationName,
	cycleLabel,
	cyclePeriod,
	vnYear,
	vnMonth,
	requests,
	loading = false,
	error = null,
	formatTime = formatPaymentRequestTime,
	partnerBankDetail,
	cycleDetails = null,
	listRowCurrencyCode = null,
}: PaymentHistoryModalProps) {
	if (!isOpen) return null;

	const modalContent = (
		<ModalOverlay onClick={onClose}>
			<ModalContainer onClick={(e) => e.stopPropagation()}>
				<Body>
					{loading ? (
						<div style={{ padding: "24px", color: "#64748b", fontSize: "15px" }}>Loading…</div>
					) : error ? (
						<div style={{ padding: "24px", color: "#b91c1c", fontSize: "15px" }}>{error}</div>
					) : (
						<PartnerPayoutDetailsContent
							stationName={stationName}
							cycleLabel={cycleLabel}
							cyclePeriod={cyclePeriod ?? undefined}
							vnYear={vnYear ?? undefined}
							vnMonth={vnMonth ?? undefined}
							requests={requests}
							formatTime={formatTime}
							partnerBankDetail={partnerBankDetail}
							cycleDetails={cycleDetails}
							listRowCurrencyCode={listRowCurrencyCode}
							headerAction={<CloseButton onClick={onClose}>×</CloseButton>}
						/>
					)}
				</Body>
			</ModalContainer>
		</ModalOverlay>
	);

	return createPortal(modalContent, document.body);
}
